/**
 * Keyboard guards shared by the surfaces that read raw key events: the
 * shortcut guard, factored out of the comment-mode "C" toggle (F-50) so the
 * tool-rail's V/C/P shortcuts (ToolRail.tsx) use the identical check instead of
 * a second copy that could drift, and the IME guards below, which every editor
 * that binds Enter must apply for the same reason.
 */

/** Is this event's target a text-entry surface (input/textarea/contenteditable)? */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return el !== null && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
}

/* ------------------------------ IME guards ------------------------------ */

/**
 * Is this keydown the INPUT METHOD's, rather than ours?
 *
 * Typing Traditional Chinese (or Japanese, or Korean) is two stages: keystrokes
 * build a *composition* the IME shows in a candidate popup, and one more key
 * picks the character that finally lands in the field. Every key of the first
 * stage is delivered as a normal `keydown` — Enter picks a candidate, Backspace
 * deletes a keystroke, the arrows walk the popup — so an editor that reads
 * `event.key` without checking gets "Enter" and splits the block the writer was
 * in the middle of a word of.
 *
 * `isComposing` is the standard signal (true for every keydown between
 * compositionstart and compositionend — including, measured in Chromium, the
 * Enter that picks a candidate, which still arrives as key "Enter"/keyCode 13).
 * `keyCode === 229` is the older one — "the IME swallowed this key" — reported
 * for keys the IME consumes, and by some IMEs for every key they see.
 */
export function isImeComposing(event: Pick<KeyboardEvent, "isComposing" | "keyCode">): boolean {
  return event.isComposing || event.keyCode === 229;
}

/**
 * The window after `compositionend` in which an Enter is still the IME's.
 * Long enough to cover the echo (same key press, microseconds later), far short
 * of a human pressing Enter again on purpose.
 */
export const COMPOSITION_ECHO_MS = 50;

/**
 * WebKit fires the composition-ending Enter a SECOND time, as a plain keydown
 * with the composing flags already cleared (the long-standing "IME Enter
 * submits the form twice" bug). Chromium doesn't, so this can only ever fire on
 * Safari — where, without it, picking a candidate with Enter still splits the
 * block. Only Enter echoes; every other key reaches the app normally.
 *
 * `msSinceCompositionEnd` is Infinity when nothing has been composed yet.
 */
export function isImeEnterEcho(key: string, msSinceCompositionEnd: number): boolean {
  return key === "Enter" && msSinceCompositionEnd < COMPOSITION_ECHO_MS;
}

/* ----------------------------- platform ----------------------------- */

/**
 * Are we on a Mac? Decides which modifier a shortcut HINT shows ("⌘Z" vs
 * "Ctrl+Z") — the handlers themselves accept either key. Read once: the
 * platform does not change while the app runs.
 */
export const isMac: boolean =
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad|ipod/i.test(
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
      ?.platform ??
      navigator.platform ??
      "",
  );
